import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, Box } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useOrgParams } from '~/common/use-org-params'
import { useSetOrgParams } from '~/common/use-set-org-params'
import { useOrgContext, useOrgContextDispatch, setHoveredOrg } from '~/common/context/organization/org-context'
import { StaticMap } from './static-map'
import { TpFullscreenFab } from '~/components/tp-fullscreen-fab'
import { useMapInspectorContext } from './context/map-inspector-context'
import { RelationshipLegend } from './relationship-legend'
import { useFeatures } from '~/common/use-features'

export const MapCard = ({ width, height, fullscreen = false, editLayer, editingPlot, showRelationshipLegend = true, orgZoom = true }) => {
  const navigate = useNavigate()
  const { farmId, plotId } = useOrgParams()
  const setOrgParams = useSetOrgParams()
  const isMapPage = location.pathname === '/map'
  const { features } = useFeatures()
  const [isMouseOver, setIsMouseOver] = useState(false)

  // Inspector context — only available inside MapInspectorProvider (fullscreen mode)
  const { relationshipCounts } = useMapInspectorContext() ?? {}
  const { activeOrgs } = useOrgContext()

  // Imperative animation ref — MapZoomController registers its flyTo fn here.
  // Calling it synchronously in the event handler starts the Leaflet animation before
  // React processes the setSearchParams state update, decoupling it from the context reducer.
  const mapAnimRef = useRef(null)
  const dispatch = useOrgContextDispatch()

  // Optimistic visual selection — updated immediately on click so the polygon highlight
  // changes at the end of the zoom animation without waiting for the URL round-trip
  // (which triggers a full app-tree re-render and adds ~300ms of delay).
  // Synced back from URL on external navigation (back/forward, deep links).
  const [visualPlotId, setVisualPlotId] = useState(plotId)
  useEffect(() => {
    function syncFromUrl() {
      setVisualPlotId(plotId)
    }
    syncFromUrl()
  }, [plotId])

  // Plot selection handler — toggles: re-clicking the active plot deselects and zooms to farm.
  // setOrgParams is deferred into the onComplete callback so URL updates (and downstream re-renders)
  // only fire after the zoom animation finishes, eliminating stutter from concurrent data loading.
  const handlePlotClick = useCallback((newPlotId) => {
    dispatch(setHoveredOrg({ hoveredOrg: null }))
    const isDeselect = newPlotId === plotId
    const targetId = isDeselect ? null : newPlotId
    const params = isDeselect ? { farmId } : { farmId, plotId: newPlotId }
    const update = () => {
      setVisualPlotId(targetId)
      setOrgParams(params)
    }
    if (mapAnimRef.current) {
      mapAnimRef.current(targetId, update)
    } else {
      update()
    }
  }, [dispatch, farmId, plotId, setOrgParams])

  // Background click handler — zoom to farm bounds first, then remove plotId from URL
  const handleMapClick = useCallback(() => {
    if (plotId) {
      dispatch(setHoveredOrg({ hoveredOrg: null }))
      const update = () => {
        setVisualPlotId(null)
        setOrgParams({ farmId })
      }
      if (mapAnimRef.current) {
        mapAnimRef.current(null, update)
      } else {
        update()
      }
    }
  }, [dispatch, farmId, plotId, setOrgParams])

  // Background click while editing — zoom to farm bounds without deselecting the active plot
  const handleMapClickZoomOnly = useCallback(() => {
    mapAnimRef.current?.(null)
  }, [])

  const handleFullscreen = () => {
    const url = farmId ? `/map?farmId=${farmId}` : '/map'
    navigate(url)
  }

  const orgName = activeOrgs?.[activeOrgs.length - 1]?.name

  const mapContent = (
    <StaticMap
      // dragging={false}
      // scrollWheelZoom={false}
      clampToBounds
      disableAutoZoom={fullscreen}
      width={width}
      height={height}
      showPlotOrganizationPolygons
      onPlotClick={handlePlotClick}
      onMapClick={editLayer ? handleMapClickZoomOnly : handleMapClick}
      editingPlot={editingPlot}
      showPolygonLabels={fullscreen}
      fullImage={isMapPage || !plotId}
      animRef={mapAnimRef}
      orgZoom={orgZoom}
      selectedPlotId={visualPlotId}
    >
      {editLayer}
    </StaticMap>
  )

  // Fullscreen mode: no Card wrapper.
  // position:absolute + inset:0 fills the position:relative parent in map-fullscreen.jsx exactly,
  // ensuring the chip at bottom:16 is always anchored over the map image.
  if (fullscreen) {
    return (
      <Box sx={{ position: 'absolute', inset: 0, mb: 0 }}>
        {mapContent}
        {showRelationshipLegend && relationshipCounts && (
          <RelationshipLegend counts={relationshipCounts} orgName={orgName} />
        )}
      </Box>
    )
  }

  // Card mode: for dashboard
  return (
    <Card
     onMouseEnter={() => setIsMouseOver(true)}
     onMouseLeave={() => setIsMouseOver(false)}
     sx={{ aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column', position: 'relative' }}
    >
      <CardContent
        sx={{
          p: 0,
          flex: 1,
          overflow: 'hidden',
          minHeight: 0,
          display: 'flex',
          '&.MuiCardContent-root:last-child': {
            pb: 0,
          }
        }}
      >
        {mapContent}
      </CardContent>

      {features['page_map'] && !isMapPage && isMouseOver && (
        <TpFullscreenFab onClick={handleFullscreen}>
          <Icons.Fullscreen />
        </TpFullscreenFab>
      )}
    </Card>
  )
}
